import gfiLogo from "/img/integration/gfi.svg"
import { ConnectMailboxButtons } from "@/connect_mailbox/connect_mailbox_buttons"
import { Button } from "@/components/ui/button.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Label } from "@/components/ui/label.tsx"
import { useAsyncEffect } from "@/utils"
import { DateTime } from "luxon"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { use_connect_redirect_to } from "../use_connect_redirect_to"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import {
  findIntegrationByName,
  getIntegrations,
  IntegrationWithJobInfo,
  upsertIntegration,
  updateIntegration,
} from "@/api/integration"

const GFI_NAME = "gfi"
// GFI has no login of its own, so there is no meaningful username — a stable placeholder keeps the
// upsert idempotent, the same trick the BOL email sync uses.
const STABLE_USERNAME = "default"

interface ConnectGfiPageProps {
  redirectTo?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ConnectGfiPage(props: any = {}) {
  const redirectTo = use_connect_redirect_to(props.redirectTo)
  const [, setLocation] = useLocation()
  const currentPath = window.location.pathname

  const [emailIntegrations, setEmailIntegrations] = useState<IntegrationWithJobInfo[]>([])
  const [gfiIntegrationId, setGfiIntegrationId] = useState<string | null>(null)
  const [supplierNumber, setSupplierNumber] = useState("")
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  useAsyncEffect(async () => {
    const res = await getIntegrations()
    if (!res.ok) {
      setLoadError("Could not load integrations")
      return
    }
    const all = res.value.data
    setEmailIntegrations(all.filter((i: IntegrationWithJobInfo) => i.name === "gmail" || i.name === "microsoft"))
    const gfi = findIntegrationByName(all, GFI_NAME)
    if (gfi) {
      setGfiIntegrationId(gfi.id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const existing = (gfi as any).data?.supplier_number
      if (typeof existing === "string") setSupplierNumber(existing)
    }
  }, [])

  async function handleSave() {
    setLoading(true)
    setLoadError(null)
    let id = gfiIntegrationId
    if (!id) {
      const created = await upsertIntegration(GFI_NAME, STABLE_USERNAME, {})
      if (!created.ok) {
        setLoadError("Could not enable the GFI connection")
        setLoading(false)
        return
      }
      id = created.value.data.id
      setGfiIntegrationId(id)
    }
    const trimmed = supplierNumber.trim()
    const dataRes = await updateIntegration(id, {
      data: { supplier_number: trimmed === "" ? null : trimmed },
      enabled: true,
    })
    if (!dataRes.ok) {
      setLoadError("Could not update settings")
      setLoading(false)
      return
    }
    setLoading(false)
    setLocation(redirectTo)
  }

  const has_mailbox = emailIntegrations.length > 0

  return (
    <CenterColumnContainer>
      <div class="space-y-6 max-w-md mx-auto">
        {/* The distributor leads, not the transport. The mail logos still appear below, on the
            buttons, where they mean "click this to add that mailbox". */}
        <div class="flex items-center justify-center">
          <img src={gfiLogo} alt="Gourmet Foods International" class="h-16 object-contain" />
        </div>
        <h1 class="text-xl font-semibold text-gray-900 text-center">Sync GFI deductions from email</h1>
        <p class="text-gray-500 text-sm text-center">
          GFI's Mantis portal holds promotions and item setup, but no deductions — every chargeback arrives as PDF
          backup on their recurring <em>"Backup for Deductions and Short Payments"</em> email. We read those from the
          mailboxes already connected below and turn each document into a deduction.
        </p>

        <div class="space-y-3">
          <p class="text-sm font-medium text-gray-800">Connect email</p>
          <ConnectMailboxButtons
            gmailLabel="Add Gmail"
            microsoftLabel="Add Microsoft 365"
          />
        </div>

        {has_mailbox ? (
          <div class="border border-gray-200 rounded-lg p-4 space-y-2">
            <h3 class="font-medium text-gray-900 text-sm">Connected mailboxes</h3>
            {emailIntegrations.map((e: IntegrationWithJobInfo) => {
              const updated = e.session_updated_at
                ? DateTime.fromISO(e.session_updated_at).toLocaleString(DateTime.DATETIME_MED)
                : ""
              return (
                <div key={e.id} class="flex justify-between gap-4 text-sm">
                  <span class="text-gray-700 truncate">
                    {e.name}: {e.username}
                  </span>
                  {updated ? <span class="text-gray-400 shrink-0">{updated}</span> : null}
                </div>
              )
            })}
          </div>
        ) : (
          <p class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            No mailbox is connected yet. GFI sends no portal feed, so this connection has nothing to read until the
            mailbox that receives their backup emails is connected above.
          </p>
        )}

        <div class="space-y-1.5">
          <Label htmlFor="gfi-supplier-number">GFI supplier number (optional)</Label>
          <Input
            id="gfi-supplier-number"
            value={supplierNumber}
            placeholder="e.g. 3052"
            onInput={(e: Event) => setSupplierNumber((e.target as HTMLInputElement).value)}
          />
          <p class="text-xs text-gray-500">
            Shown on GFI's billback reports next to your company name. Optional — it is used to spot backup that belongs
            to a different supplier, not to find your own.
          </p>
        </div>

        {loadError ? <p class="text-sm text-red-600">{loadError}</p> : null}

        <div class="flex justify-between gap-3">
          <Button type="button" variant="outline" onClick={() => setLocation(redirectTo)}>
            Back
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={loading} class="bg-gray-900 text-white">
            {loading ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </CenterColumnContainer>
  )
}
